import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../cn.js';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label: ReactNode;
  /** Shown under the field. Announced to screen readers via aria-describedby. */
  hint?: ReactNode;
  /** Shown under the field in the danger tone and sets aria-invalid. */
  error?: ReactNode;
  /** Leading glyph inside the field — an icon, never decorative text. */
  icon?: ReactNode;
  /** Trailing content inside the field — a unit suffix or an icon-button (e.g. show/hide password). */
  suffix?: ReactNode;
}

/**
 * A labelled text input.
 *
 * The label is REQUIRED, not optional, and rendered as a real `<label for>`: an input identified
 * only by its placeholder has no accessible name once someone types into it, and the admin area
 * is keyboard-first (CLAUDE.md, accessibility). The height comes from `--control-height` so it
 * tracks the density switch — a compact table's filter row gets shorter inputs without the screen
 * knowing it is in compact mode.
 *
 * `icon`/`suffix` are the auth screens' addition — a leading glyph and a trailing slot (a unit, or
 * an icon-button like show/hide password) inside the field itself, alongside `hint`/`error` below
 * it. The two additions don't interact: icon/suffix only ever affect the input's own padding.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, icon, suffix, className, id: idProp, ...rest },
  ref,
) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {label}
      </label>
      <span className="relative flex items-center">
        {icon && (
          <span className="pointer-events-none absolute left-3 flex text-fg-subtle">{icon}</span>
        )}
        <input
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          className={cn(
            'h-[var(--control-height)] w-full rounded-md border bg-surface text-base text-fg',
            'placeholder:text-fg-subtle',
            'transition-colors duration-[var(--duration-fast)]',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'read-only:bg-bg-subtle read-only:text-fg-muted',
            icon ? 'pl-9' : 'pl-3',
            suffix ? 'pr-9' : 'pr-3',
            error ? 'border-danger' : 'border-border hover:border-border-strong',
            className,
          )}
          {...rest}
        />
        {suffix && <span className="absolute right-2 flex items-center">{suffix}</span>}
      </span>
      {error ? (
        <p id={errorId} className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs text-fg-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
